import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "./images/logo.jpg";
import ProfileDropdown from "./ProfileDropdown";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from "../hooks/useTranslation";
import jwtTokenService from "../services/jwtTokenService";
import {
  FaHome,
  FaUtensils,
  FaBed,
  FaInfoCircle,
  FaEnvelope,
  FaQuestionCircle,
  FaBars,
  FaTimes,
} from "react-icons/fa";
import { Star, ChevronDown, User, LogOut } from "lucide-react";

function NavScrollExample() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isProfileSubmenuOpen, setIsProfileSubmenuOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Check authentication status on component mount
  useEffect(() => {
    const checkAuthStatus = () => {
      // Check JWT token authentication
      const authenticated = jwtTokenService.isAuthenticated();
      setIsLoggedIn(authenticated);
    };

    checkAuthStatus();

    // Listen for storage changes (in case user logs in/out in another tab)
    const handleStorageChange = () => {
      checkAuthStatus();
    };

    // Listen for custom login/logout events (triggered by JWT service)
    const handleLoginEvent = () => {
      checkAuthStatus();
    };

    const handleLogoutEvent = () => {
      checkAuthStatus();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("login", handleLoginEvent);
    window.addEventListener("logout", handleLogoutEvent);

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("login", handleLoginEvent);
      window.removeEventListener("logout", handleLogoutEvent);
    };
  }, []);

  const handleLogout = () => {
    // Use JWT token service to properly logout
    jwtTokenService.logout();

    // Update state
    setIsLoggedIn(false);

    // Redirect to home page
    navigate("/");

    // Reload the page to ensure all components update
    window.location.reload();
  };

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Prevent body scroll when menu is open
    if (!isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = "auto";
  };

  const navItems = [
    { path: "/", label: t("home"), icon: FaHome },
    { path: "/restaurants", label: t("restaurants"), icon: FaUtensils },
    { path: "/HotelRoomsPage", label: t("Hotels"), icon: FaBed },
    { path: "/about_us", label: t("about"), icon: FaInfoCircle },
    { path: "/Contact", label: t("contact"), icon: FaEnvelope },
    { path: "/Faqs", label: t("faqs"), icon: FaQuestionCircle },
  ];

  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: scrolled
          ? "rgba(255, 255, 255, 0.95)"
          : "rgba(255, 255, 255, 0.98)",
        backdropFilter: "blur(10px)",
        borderBottom: scrolled
          ? "1px solid rgba(238, 46, 36, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.08)",
        boxShadow: scrolled
          ? "0 4px 20px rgba(238, 46, 36, 0.1)"
          : "0 2px 15px rgba(0, 0, 0, 0.05)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "0 clamp(1rem, 3vw, 2rem)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "70px",
          gap: "clamp(1rem, 3vw, 2rem)",
        }}
      >
        {/* Logo Section */}
        <Link
          to="/"
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: "clamp(8px, 2vw, 12px)",
            transition: "transform 0.2s ease",
            flexShrink: 0,
            marginRight: "clamp(0.5rem, 2vw, 1rem)",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.transform = "scale(1.02)")
          }
          onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
        >
          <img
            src={logo}
            alt="Trivana Logo"
            style={{
              height: "clamp(35px, 8vw, 45px)",
              width: "clamp(35px, 8vw, 45px)",
              borderRadius: "12px",
              objectFit: "cover",
              boxShadow: "0 4px 12px rgba(238, 46, 36, 0.25)",
            }}
          />
          <div
            style={{
              background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontSize: "clamp(16px, 3.5vw, 22px)",
              fontWeight: "800",
              letterSpacing: "-0.8px",
              textTransform: "none",
              fontFamily: "'Inter', sans-serif",
              whiteSpace: "nowrap",
            }}
          >
            BookTrivana.com
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div
          className="desktop-nav"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                style={{
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "clamp(8px, 2vw, 10px) clamp(12px, 2.5vw, 16px)",
                  borderRadius: "12px",
                  color: "#2E2E2E",
                  fontWeight: "600",
                  fontSize: "clamp(0.8rem, 1.8vw, 0.9rem)",
                  letterSpacing: "0.3px",
                  fontFamily: "'Inter', sans-serif",
                  transition: "all 0.2s ease",
                  position: "relative",
                  overflow: "hidden",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, rgba(238, 46, 36, 0.08) 0%, rgba(255, 196, 0, 0.08) 100%)";
                  e.currentTarget.style.color = "#EE2E24";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#2E2E2E";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <IconComponent size={16} />
                {item.label}
              </Link>
            );
          })}

          {/* Language Switcher */}
          <div style={{ marginLeft: "8px" }}>
            <LanguageSwitcher />
          </div>

          {/* Authentication Section */}
          <div style={{ marginLeft: "8px" }}>
            {isLoggedIn ? (
              <ProfileDropdown onLogout={handleLogout} />
            ) : (
              <Link
                to="/Login"
                style={{
                  textDecoration: "none",
                  background:
                    "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                  color: "white",
                  padding: "clamp(8px, 2vw, 10px) clamp(18px, 3.5vw, 24px)",
                  borderRadius: "25px",
                  fontWeight: "700",
                  fontSize: "clamp(0.8rem, 1.8vw, 0.9rem)",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 15px rgba(238, 46, 36, 0.3)",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "'Inter', sans-serif",
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 6px 20px rgba(238, 46, 36, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow =
                    "0 4px 15px rgba(238, 46, 36, 0.3)";
                }}
              >
                {t("login")}
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="mobile-menu-btn"
          style={{
            display: "none",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
            border: "none",
            borderRadius: "10px",
            width: "45px",
            height: "45px",
            color: "white",
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "0 4px 12px rgba(238, 46, 36, 0.3)",
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = "scale(1)";
          }}
        >
          {isMobileMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          id="mobile-menu-overlay"
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            width: "100vw",
            height: "100vh",
            background: "linear-gradient(180deg, #F9FAFB 0%, #F3F4F6 100%)",
            zIndex: 2147483647,
            overflowY: "auto",
            padding: "0",
            margin: "0",
            display: "block",
            animation: "slideInRight 0.3s ease-out",
          }}
        >
          {/* Header with Menu title and Close Button */}
          <div
            style={{
              background: "linear-gradient(135deg, #EE2E24 0%, #C41E3A 100%)",
              padding: "10px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 4px 20px rgba(238, 46, 36, 0.25)",
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.2rem",
                }}
              >
                ☰
              </div>
              <span
                style={{
                  fontSize: "1.1rem",
                  fontWeight: "700",
                  color: "#FFFFFF",
                  letterSpacing: "0.5px",
                }}
              >
                MENU
              </span>
            </div>
            <button
              onClick={closeMobileMenu}
              style={{
                background: "rgba(255, 255, 255, 0.95)",
                border: "none",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                color: "#EE2E24",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "bold",
                fontSize: "1.3rem",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "rotate(90deg) scale(1.1)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "rotate(0deg) scale(1)";
              }}
            >
              ✕
            </button>
          </div>

          {/* Enterprise-Level Menu Content */}
          <div
            style={{
              background: "#FFFFFF",
              minHeight: "calc(100vh - 60px)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* 1. Profile Header Section - Production Ready & Interactive */}
            <div
              style={{
                padding: "24px 20px",
                background: "linear-gradient(135deg, #EE2E24 0%, #C41E3A 100%)",
                color: "#FFFFFF",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  cursor: "pointer",
                }}
                onClick={() => setIsProfileSubmenuOpen(!isProfileSubmenuOpen)}
              >
                {isLoggedIn ? (
                  <>
                    <img
                      src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face"
                      alt="User Profile"
                      style={{
                        width: "56px",
                        height: "56px",
                        borderRadius: "50%",
                        border: "3px solid rgba(255, 255, 255, 0.3)",
                        boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "1.1rem",
                          fontWeight: "800",
                          letterSpacing: "-0.2px",
                        }}
                      >
                        Ganesh Swami
                      </p>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                          marginTop: "4px",
                          fontSize: "0.75rem",
                          fontWeight: "700",
                          background: "rgba(255, 255, 255, 0.15)",
                          padding: "3px 10px",
                          borderRadius: "20px",
                          width: "fit-content",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        <Star size={10} fill="#FFC400" stroke="#FFC400" />
                        Basic Member
                      </div>
                    </div>
                    <ChevronDown
                      size={20}
                      style={{
                        opacity: 0.7,
                        transition: "transform 0.3s ease",
                        transform: isProfileSubmenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </>
                ) : (
                  <div
                    onClick={() => {
                      closeMobileMenu();
                      navigate("/Login");
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      cursor: "pointer",
                      width: "100%",
                    }}
                  >
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        borderRadius: "50%",
                        background: "rgba(255, 255, 255, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <User size={24} color="#FFFFFF" />
                    </div>
                    <div>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "1rem",
                          fontWeight: "800",
                        }}
                      >
                        Sign in to your account
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.75rem",
                          fontWeight: "500",
                          opacity: 0.8,
                        }}
                      >
                        Access your bookings & rewards
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Profile Submenu (Mobile Profile Modal) */}
              {isLoggedIn && isProfileSubmenuOpen && (
                <div
                  style={{
                    marginTop: "16px",
                    background: "rgba(255, 255, 255, 0.1)",
                    borderRadius: "12px",
                    overflow: "hidden",
                    animation: "slideDown 0.2s ease-out",
                  }}
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      closeMobileMenu();
                      navigate("/profile");
                    }}
                    style={{
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    }}
                  >
                    <User size={16} /> View Profile
                  </div>
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogout();
                    }}
                    style={{
                      padding: "12px 16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      fontSize: "0.9rem",
                      fontWeight: "600",
                      color: "#FFC400",
                    }}
                  >
                    <LogOut size={16} /> Sign Out
                  </div>
                </div>
              )}
            </div>

            {/* 2. Quick Settings Bar (Language Switcher Upper Placement) */}
            <div
              style={{
                padding: "12px 20px",
                background: "#F9FAFB",
                borderBottom: "1px solid #F3F4F6",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <LanguageSwitcher />
              </div>
              <div
                style={{
                  fontSize: "0.7rem",
                  color: "#9CA3AF",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Quick Settings
              </div>
            </div>

            {/* 3. Main Navigation List with Descriptions */}
            <div
              style={{
                flex: 1,
                padding: "8px 0",
                overflowY: "auto",
              }}
            >
              {[
                {
                  ...navItems[0],
                  desc: "Return to home page",
                },
                {
                  ...navItems[1],
                  desc: "Explore top cuisines",
                },
                {
                  ...navItems[2],
                  desc: "Book your luxury stay",
                },
                {
                  ...navItems[3],
                  desc: "Learn about Trivana",
                },
                {
                  ...navItems[4],
                  desc: "Get in touch with us",
                },
                {
                  ...navItems[5],
                  desc: "Common questions",
                },
              ].map((item, idx) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={item.path}
                    onClick={() => {
                      closeMobileMenu();
                      navigate(item.path);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "14px 20px",
                      gap: "18px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#F9FAFB";
                      e.currentTarget.style.paddingLeft = "24px";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.paddingLeft = "20px";
                    }}
                  >
                    <div
                      style={{
                        color: "#EE2E24",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconComponent size={22} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.95rem",
                          fontWeight: "700",
                          color: "#111827",
                        }}
                      >
                        {item.label}
                      </p>
                      <p
                        style={{
                          margin: "2px 0 0 0",
                          fontSize: "0.75rem",
                          color: "#6B7280",
                          fontWeight: "500",
                        }}
                      >
                        {item.desc}
                      </p>
                    </div>
                    <span
                      style={{
                        color: "#E5E7EB",
                        fontSize: "1.2rem",
                        fontWeight: "300",
                      }}
                    >
                      ›
                    </span>
                  </div>
                );
              })}
            </div>

            {/* 4. Support Bar - Global Bottom */}
            <div
              style={{
                padding: "20px",
                borderTop: "1.5px solid #F3F4F6",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  padding: "12px",
                  background: "#F9FAFB",
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/Contact")}
              >
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "#EE2E24",
                    boxShadow: "0 0 8px rgba(238, 46, 36, 0.4)",
                  }}
                />
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.78rem",
                    color: "#374151",
                    fontWeight: "700",
                  }}
                >
                  Need help? <span style={{ color: "#EE2E24" }}>Contact Support</span>
                </p>
              </div>

              <div style={{ textAlign: "center", opacity: 0.3 }}>
                <p style={{ margin: 0, fontSize: "0.6rem", fontWeight: "700", letterSpacing: "1px" }}>
                  BOOKTRIVANA v2.0.4
                </p>
              </div>
            </div>
          </div>




        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: flex !important;
          }
        }
      `}</style>
    </nav>
  );
}

export default NavScrollExample;
